import { Injectable, inject, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { UserService } from './user.service';

export type PreferencesSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** How long to wait after the last change before auto-saving, so rapid
 *  clicks (ticking several rows) are batched into one request. */
const AUTO_SAVE_DELAY_MS = 500;

/** One selectable row inside a "Select" panel (Preferences > Experienced In,
 *  etc.) — grouped by `type` in the table, exactly like the mockup. */
export interface PreferenceOption {
  type: string;
  activity: string;
  description: string;
}

export type PreferenceCategoryKey =
  | 'interestedIn'
  | 'experiencedIn'
  | 'curiousAbout'
  | 'softLimits'
  | 'hardLimits'
  | 'identities'
  | 'roles'
  | 'dynamics';

export interface PreferenceCategoryMeta {
  key: PreferenceCategoryKey;
  /** Row label + panel/breadcrumb title, e.g. "Experienced In". */
  label: string;
  /** URL slug for /dashboard/preferences/:slug, e.g. "experienced-in". */
  slug: string;
}

export const PREFERENCE_CATEGORIES: PreferenceCategoryMeta[] = [
  { key: 'interestedIn',  label: 'Interested In',  slug: 'interested-in' },
  { key: 'experiencedIn', label: 'Experienced In', slug: 'experienced-in' },
  { key: 'curiousAbout',  label: 'Curious About',  slug: 'curious-about' },
  { key: 'softLimits',    label: 'Soft Limits',    slug: 'soft-limits' },
  { key: 'hardLimits',    label: 'Hard Limits',    slug: 'hard-limits' },
  { key: 'identities',    label: 'Identities',     slug: 'identities' },
  { key: 'roles',         label: 'Roles',          slug: 'roles' },
  { key: 'dynamics',      label: 'Dynamics',       slug: 'dynamics' },
];

/**
 * ── Option data ──────────────────────────────────────────────────────────
 *
 * "Interested In", "Experienced In", "Curious About", "Soft Limits" and
 * "Hard Limits" all share ONE master option list (per spec 2c) — the full
 * kink/fetish taxonomy supplied directly (comprehensive_kink_fetish_taxonomy
 * PDF), 17 categories / 168 rows. Some activities (e.g. "Blood play",
 * "Fear play", "Extreme pain") legitimately appear under more than one
 * category in the source taxonomy; they're kept as separate rows here (so
 * both categories show them in the panel) but share one selection — picking
 * either row selects the same underlying value.
 */
export const SHARED_ACTIVITY_OPTIONS: PreferenceOption[] = [
  // ── BDSM & Power Exchange ──────────────────────────────────────────────
  { type: 'BDSM & Power Exchange', activity: 'Dominance', description: 'Power and authority dynamics' },
  { type: 'BDSM & Power Exchange', activity: 'Submission', description: 'Voluntary power exchange' },
  { type: 'BDSM & Power Exchange', activity: 'Switching', description: 'Enjoying dominant and submissive roles' },
  { type: 'BDSM & Power Exchange', activity: 'Master/slave dynamics', description: 'Formalized power exchange' },
  { type: 'BDSM & Power Exchange', activity: 'Long-term D/s', description: 'Relationship-based power exchange' },
  { type: 'BDSM & Power Exchange', activity: '24/7 dynamics', description: 'Power exchange extending beyond scenes' },
  { type: 'BDSM & Power Exchange', activity: 'Service submission', description: 'Submission expressed through service' },
  { type: 'BDSM & Power Exchange', activity: 'Service dominance', description: 'Dominance expressed through directing service' },
  { type: 'BDSM & Power Exchange', activity: 'Protocol', description: 'Structured behavioural expectations' },
  { type: 'BDSM & Power Exchange', activity: 'Collaring', description: 'Symbolic commitment or ownership' },
  { type: 'BDSM & Power Exchange', activity: 'Ownership', description: 'Consensual symbolic ownership' },
  { type: 'BDSM & Power Exchange', activity: 'Authority play', description: 'Role-based authority' },
  { type: 'BDSM & Power Exchange', activity: 'Brat dynamics', description: 'Provocation and playful resistance' },
  { type: 'BDSM & Power Exchange', activity: 'Caregiver dynamics', description: 'Adult caregiving roleplay' },
  { type: 'BDSM & Power Exchange', activity: 'Gentle dominance', description: 'Low-intensity or nurturing dominance' },
  { type: 'BDSM & Power Exchange', activity: 'Female domination', description: 'Woman-led dominance' },
  { type: 'BDSM & Power Exchange', activity: 'Male domination', description: 'Man-led dominance' },
  { type: 'BDSM & Power Exchange', activity: 'Erotic discipline', description: 'Rules and consensual consequences' },
  { type: 'BDSM & Power Exchange', activity: 'Training', description: 'Structured learning within a dynamic' },
  { type: 'BDSM & Power Exchange', activity: 'Contracts & rituals', description: 'Symbolic formalization of a dynamic' },

  // ── Bondage & Restraint ────────────────────────────────────────────────
  { type: 'Bondage & Restraint', activity: 'Rope bondage', description: 'Restraint using rope' },
  { type: 'Bondage & Restraint', activity: 'Shibari', description: 'Japanese rope-art tradition' },
  { type: 'Bondage & Restraint', activity: 'Handcuffs', description: 'Metal or toy restraints' },
  { type: 'Bondage & Restraint', activity: 'Wrist/ankle restraints', description: 'Restraining limbs' },
  { type: 'Bondage & Restraint', activity: 'Full-body restraint', description: 'Restricting movement across the body' },
  { type: 'Bondage & Restraint', activity: 'Mummification', description: 'Extensive body wrapping' },
  { type: 'Bondage & Restraint', activity: 'Predicament bondage', description: 'Restriction involving uncomfortable choices or positions' },
  { type: 'Bondage & Restraint', activity: 'Suspension', description: 'Suspending a person in bondage' },
  { type: 'Bondage & Restraint', activity: 'Blindfolding', description: 'Removing visual input' },
  { type: 'Bondage & Restraint', activity: 'Gags', description: 'Restricting speech' },
  { type: 'Bondage & Restraint', activity: 'Leash play', description: 'Leading or guiding a restrained person' },
  { type: 'Bondage & Restraint', activity: 'Confinement', description: 'Restricted physical space' },
  { type: 'Bondage & Restraint', activity: 'Immobility', description: 'Restriction of movement' },

  // ── Impact & Pain ──────────────────────────────────────────────────────
  { type: 'Impact & Pain', activity: 'Impact play', description: 'Broad category of consensual striking' },
  { type: 'Impact & Pain', activity: 'Spanking', description: 'Striking with hand or implement' },
  { type: 'Impact & Pain', activity: 'Flogging', description: 'Multi-tailed impact implement' },
  { type: 'Impact & Pain', activity: 'Caning', description: 'Cane-based impact' },
  { type: 'Impact & Pain', activity: 'Whipping', description: 'Whip-based impact' },
  { type: 'Impact & Pain', activity: 'Slapping', description: 'Hand-based impact' },
  { type: 'Impact & Pain', activity: 'Paddling', description: 'Paddle-based impact' },
  { type: 'Impact & Pain', activity: 'Hair pulling', description: 'Pain/control through hair' },
  { type: 'Impact & Pain', activity: 'Rough play', description: 'Physically forceful consensual play' },
  { type: 'Impact & Pain', activity: 'Sadism', description: 'Enjoyment of consensually inflicting pain' },
  { type: 'Impact & Pain', activity: 'Masochism', description: 'Enjoyment of consensually receiving pain' },
  { type: 'Impact & Pain', activity: 'Extreme pain', description: 'High-intensity consensual pain' },

  // ── Sensory & Physical ─────────────────────────────────────────────────
  { type: 'Sensory & Physical', activity: 'Sensory play', description: 'Using varied sensory stimulation' },
  { type: 'Sensory & Physical', activity: 'Sensory deprivation', description: 'Reducing sensory input' },
  { type: 'Sensory & Physical', activity: 'Temperature play', description: 'Temperature-based sensation' },
  { type: 'Sensory & Physical', activity: 'Wax play', description: 'Wax-based sensation' },
  { type: 'Sensory & Physical', activity: 'Tickling', description: 'Tickling as sensation/control' },
  { type: 'Sensory & Physical', activity: 'Massage', description: 'Massage as sensual or power-play context' },
  { type: 'Sensory & Physical', activity: 'Trampling', description: 'Consensual body-weight play' },
  { type: 'Sensory & Physical', activity: 'Facesitting', description: 'Body-weight dominance' },
  { type: 'Sensory & Physical', activity: 'Messy play', description: 'Mess-oriented sensory play' },
  { type: 'Sensory & Physical', activity: 'Water play', description: 'Water-based sensory play' },

  // ── Psychological & Verbal ─────────────────────────────────────────────
  { type: 'Psychological & Verbal', activity: 'Humiliation', description: 'Consensual embarrassment or status reduction' },
  { type: 'Psychological & Verbal', activity: 'Degradation', description: 'Consensual derogatory treatment' },
  { type: 'Psychological & Verbal', activity: 'Praise', description: 'Eroticized praise' },
  { type: 'Psychological & Verbal', activity: 'Dirty talk', description: 'Sexualized language' },
  { type: 'Psychological & Verbal', activity: 'Commands', description: 'Verbal control' },
  { type: 'Psychological & Verbal', activity: 'Name/title play', description: 'Titles and forms of address' },
  { type: 'Psychological & Verbal', activity: 'Mind games', description: 'Mental challenge or manipulation within agreed boundaries' },
  { type: 'Psychological & Verbal', activity: 'Fear play', description: 'Consensual fear-based scenarios' },
  { type: 'Psychological & Verbal', activity: 'Interrogation roleplay', description: 'Authority/psychological scenario' },
  { type: 'Psychological & Verbal', activity: 'Confession play', description: 'Structured confession scenario' },
  { type: 'Psychological & Verbal', activity: 'Objectification', description: 'Being treated symbolically as an object' },
  { type: 'Psychological & Verbal', activity: 'Human furniture', description: 'Objectification through furniture roles' },
  { type: 'Psychological & Verbal', activity: 'Conditioning', description: 'Reinforcement of agreed behaviours' },
  { type: 'Psychological & Verbal', activity: 'Mind-control fantasy', description: 'Fictional or roleplayed mental control' },

  // ── Roleplay & Transformation ──────────────────────────────────────────
  { type: 'Roleplay & Transformation', activity: 'Roleplay', description: 'Broad scenario-based roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Teacher/student', description: 'Authority roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Doctor/patient', description: 'Clinical roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Police/prisoner', description: 'Law-enforcement scenario' },
  { type: 'Roleplay & Transformation', activity: 'Kidnap/capture fantasy', description: 'Fictional capture scenario' },
  { type: 'Roleplay & Transformation', activity: 'Rescue/escape', description: 'Capture-and-rescue roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Uniform roleplay', description: 'Uniform-based roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Cosplay', description: 'Character-based roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Gender play', description: 'Roleplay involving gender presentation' },
  { type: 'Roleplay & Transformation', activity: 'Cross-dressing', description: 'Clothing/gender-expression play' },
  { type: 'Roleplay & Transformation', activity: 'Dollification', description: 'Transformation into a doll-like persona' },
  { type: 'Roleplay & Transformation', activity: 'Object transformation', description: 'Fantasy of becoming an object' },
  { type: 'Roleplay & Transformation', activity: 'Hypnosis', description: 'Consensual hypnotic roleplay' },
  { type: 'Roleplay & Transformation', activity: 'Age play', description: 'Roleplay involving adult participants adopting age-coded roles' },
  { type: 'Roleplay & Transformation', activity: 'Regression', description: 'Adopting a younger-feeling or dependent persona; adults only' },

  // ── Animal & Primal Roleplay ───────────────────────────────────────────
  { type: 'Animal & Primal Roleplay', activity: 'Pet play', description: 'Animal-roleplay dynamic' },
  { type: 'Animal & Primal Roleplay', activity: 'Puppy play', description: 'Dog-themed pet play' },
  { type: 'Animal & Primal Roleplay', activity: 'Kitten play', description: 'Cat-themed pet play' },
  { type: 'Animal & Primal Roleplay', activity: 'Pony play', description: 'Pony-themed roleplay' },
  { type: 'Animal & Primal Roleplay', activity: 'Furry roleplay', description: 'Anthropomorphic animal characters' },
  { type: 'Animal & Primal Roleplay', activity: 'Creature play', description: 'Other animal/creature personas' },
  { type: 'Animal & Primal Roleplay', activity: 'Primal play', description: 'Instinctive/hunter-style roleplay' },
  { type: 'Animal & Primal Roleplay', activity: 'Hunter/prey', description: 'Primal pursuit dynamic' },
  { type: 'Animal & Primal Roleplay', activity: 'Pet training', description: 'Training within pet-play dynamics' },

  // ── Fetish: Body & Worship ─────────────────────────────────────────────
  { type: 'Fetish: Body & Worship', activity: 'Feet', description: 'Feet-focused attraction or worship' },
  { type: 'Fetish: Body & Worship', activity: 'Hands', description: 'Hands-focused attraction' },
  { type: 'Fetish: Hair', activity: 'Hair fetish', description: 'Hair-focused attraction' },
  { type: 'Fetish: Body & Worship', activity: 'Muscle worship', description: 'Muscle-focused attraction' },
  { type: 'Fetish: Body & Worship', activity: 'Body worship', description: 'General body-focused worship' },
  { type: 'Fetish: Body & Worship', activity: 'Breast worship', description: 'Breast-focused attraction' },
  { type: 'Fetish: Body & Worship', activity: 'Buttock worship', description: 'Buttock-focused attraction' },
  { type: 'Fetish: Body & Worship', activity: 'Scent fetish', description: 'Attraction to particular scents' },
  { type: 'Fetish: Body & Worship', activity: 'Sweat fetish', description: 'Attraction to sweat/body scent' },
  { type: 'Fetish: Body & Worship', activity: 'Voice fetish', description: 'Attraction to voices or speech' },
  { type: 'Fetish: Body & Worship', activity: 'Armpit fetish', description: 'Armpit-focused attraction' },
  { type: 'Fetish: Body & Worship', activity: 'Body-hair fetish', description: 'Attraction to body hair' },
  { type: 'Fetish: Body & Worship', activity: 'Pregnancy fetish', description: 'Pregnancy-focused attraction' },

  // ── Fetish: Clothing & Materials ───────────────────────────────────────
  { type: 'Fetish: Clothing & Materials', activity: 'Latex', description: 'Latex clothing/material' },
  { type: 'Fetish: Clothing & Materials', activity: 'Rubber', description: 'Rubber clothing/material' },
  { type: 'Fetish: Clothing & Materials', activity: 'PVC', description: 'PVC clothing/material' },
  { type: 'Fetish: Clothing & Materials', activity: 'Leather', description: 'Leather clothing/material' },
  { type: 'Fetish: Clothing & Materials', activity: 'Nylon', description: 'Nylon clothing/material' },
  { type: 'Fetish: Clothing & Materials', activity: 'Stockings', description: 'Hosiery-focused fetish' },
  { type: 'Fetish: Clothing & Materials', activity: 'Tights', description: 'Tights-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Lingerie', description: 'Lingerie-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Corsets', description: 'Corset-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Uniforms', description: 'Uniform-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Gloves', description: 'Glove-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Boots', description: 'Boot-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Shoes', description: 'Footwear-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Sneakers', description: 'Sneaker-focused attraction' },
  { type: 'Fetish: Clothing & Materials', activity: 'Spandex/Lycra', description: 'Tight synthetic clothing fetish' },
  { type: 'Fetish: Clothing & Materials', activity: 'Masks', description: 'Masks as fetish objects' },

  // ── Sexual Control & Teasing ───────────────────────────────────────────
  { type: 'Sexual Control & Teasing', activity: 'Chastity', description: 'Control or restriction of sexual access' },
  { type: 'Sexual Control & Teasing', activity: 'Denial', description: 'Delaying or restricting gratification' },
  { type: 'Sexual Control & Teasing', activity: 'Edging', description: 'Delaying climax' },
  { type: 'Sexual Control & Teasing', activity: 'Teasing', description: 'Deliberate anticipation/frustration' },
  { type: 'Sexual Control & Teasing', activity: 'Orgasm control', description: 'Another person\'s control over timing/frequency' },
  { type: 'Sexual Control & Teasing', activity: 'Forced-choice play', description: 'Consensual dilemmas within roleplay' },
  { type: 'Sexual Control & Teasing', activity: 'Object control', description: 'Dominant control over selected objects or presentation' },

  // ── Exhibition, Voyeurism & Performance ────────────────────────────────
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Voyeurism', description: 'Watching others consensually' },
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Exhibitionism', description: 'Being watched or seen consensually' },
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Public play', description: 'Sexual/kink activity in contexts where privacy and consent are maintained' },
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Erotic photography', description: 'Being photographed or photographing' },
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Striptease', description: 'Performance-based teasing' },
  { type: 'Exhibition, Voyeurism & Performance', activity: 'Dance/performance', description: 'Erotic performance' },

  // ── Relationship & Group Dynamics ──────────────────────────────────────
  { type: 'Relationship & Group Dynamics', activity: 'Polyamory', description: 'Consensual multi-partner relationships' },
  { type: 'Relationship & Group Dynamics', activity: 'Open relationships', description: 'Consensual non-monogamy' },
  { type: 'Relationship & Group Dynamics', activity: 'Group play', description: 'Consensual participation by multiple adults' },
  { type: 'Relationship & Group Dynamics', activity: 'Threesomes', description: 'Three-person sexual dynamic' },
  { type: 'Relationship & Group Dynamics', activity: 'Partner swapping', description: 'Consensual partner exchange' },
  { type: 'Relationship & Group Dynamics', activity: 'Compersion', description: 'Positive feelings around a partner\'s other relationships' },
  { type: 'Relationship & Group Dynamics', activity: 'Jealousy play', description: 'Consensual exploration of jealousy' },
  { type: 'Relationship & Group Dynamics', activity: 'Competition', description: 'Consensual competitive dynamics' },

  // ── Medical & Body Modification ────────────────────────────────────────
  { type: 'Medical & Body Modification', activity: 'Medical play', description: 'Medical-themed roleplay' },
  { type: 'Medical & Body Modification', activity: 'Needle play', description: 'Needle-focused kink' },
  { type: 'Medical & Body Modification', activity: 'Piercing fetish', description: 'Attraction to piercings' },
  { type: 'Medical & Body Modification', activity: 'Tattoos', description: 'Tattoo-focused attraction' },
  { type: 'Medical & Body Modification', activity: 'Body modification', description: 'Body-modification-focused attraction' },
  { type: 'Medical & Body Modification', activity: 'Marking', description: 'Temporary symbolic/body marks' },
  { type: 'Medical & Body Modification', activity: 'Blood play', description: 'Blood-focused kink' },
  { type: 'Medical & Body Modification', activity: 'Scarification fetish', description: 'Attraction to scarification' },

  // ── Fluid, Mess & Material Play ────────────────────────────────────────
  { type: 'Fluid, Mess & Material Play', activity: 'Saliva play', description: 'Saliva-focused play' },
  { type: 'Fluid, Mess & Material Play', activity: 'Sweat play', description: 'Sweat-focused play' },
  { type: 'Fluid, Mess & Material Play', activity: 'Food play', description: 'Food used as a sensory/roleplay element' },
  { type: 'Fluid, Mess & Material Play', activity: 'Mud play', description: 'Mud-focused messy play' },
  { type: 'Fluid, Mess & Material Play', activity: 'Slime/goo', description: 'Texture-focused messy play' },

  // ── Financial & Object Dynamics ────────────────────────────────────────
  { type: 'Financial & Object Dynamics', activity: 'Financial domination', description: 'Financial control/fetish' },
  { type: 'Financial & Object Dynamics', activity: 'Tribute', description: 'Financially symbolic offerings' },
  { type: 'Financial & Object Dynamics', activity: 'Object worship', description: 'Worship of objects' },
  { type: 'Financial & Object Dynamics', activity: 'Human objectification', description: 'Treating a person symbolically as an object' },
  { type: 'Financial & Object Dynamics', activity: 'Service exchange', description: 'Service or gifts as part of a dynamic' },

  // ── Edge / High-Risk Themes ────────────────────────────────────────────
  { type: 'Edge / High-Risk Themes', activity: 'Consensual non-consent', description: 'Fantasy of non-consensual scenarios with prior consent and boundaries' },
  { type: 'Edge / High-Risk Themes', activity: 'Breath play', description: 'Breath-related kink' },
  { type: 'Edge / High-Risk Themes', activity: 'Fear play', description: 'Fear-based roleplay' },
  { type: 'Edge / High-Risk Themes', activity: 'Blood play', description: 'Blood-focused kink' },
  { type: 'Edge / High-Risk Themes', activity: 'Extreme pain', description: 'Very intense pain play' },
  { type: 'Edge / High-Risk Themes', activity: 'Kidnap fantasy', description: 'Fictional abduction scenario' },
  { type: 'Edge / High-Risk Themes', activity: 'Confinement fantasy', description: 'Fictional confinement scenario' },
];

/** Identities / Roles / Dynamics each have their OWN option list (spec 2d),
 *  supplied directly (identity_taxonomy / roles_taxonomy / dynamics_taxonomy
 *  PDFs) — 33, 27 and 21 rows respectively. */
export const IDENTITIES_OPTIONS: PreferenceOption[] = [
  // ── Core ────────────────────────────────────────────────────────────────
  { type: 'Core', activity: 'Dominant', description: 'Overall power-exchange identity' },
  { type: 'Core', activity: 'Submissive', description: 'Overall power-exchange identity' },
  { type: 'Core', activity: 'Switch', description: 'May enjoy dominant and submissive roles' },
  { type: 'Core', activity: 'Sadist', description: 'Identity centred on consensual giving of pain' },
  { type: 'Core', activity: 'Masochist', description: 'Identity centred on consensual receiving of pain' },
  { type: 'Core', activity: 'Primal', description: 'Instinctive or animalistic style of play' },

  // ── Rope ────────────────────────────────────────────────────────────────
  { type: 'Rope', activity: 'Rigger', description: 'Person who primarily ties' },
  { type: 'Rope', activity: 'Rope Bunny', description: 'Person who is primarily tied' },

  // ── Pet Play ────────────────────────────────────────────────────────────
  { type: 'Pet Play', activity: 'Pet', description: 'Animal-roleplay identity' },
  { type: 'Pet Play', activity: 'Puppy', description: 'Dog-themed pet identity' },
  { type: 'Pet Play', activity: 'Kitten', description: 'Cat-themed pet identity' },
  { type: 'Pet Play', activity: 'Pony', description: 'Pony-themed pet identity' },
  { type: 'Pet Play', activity: 'Handler', description: 'Person who guides a pet' },
  { type: 'Pet Play', activity: 'Pet Trainer', description: 'Training-oriented handler' },

  // ── D/s ─────────────────────────────────────────────────────────────────
  { type: 'D/s', activity: 'Master', description: 'Master title/identity' },
  { type: 'D/s', activity: 'Mistress', description: 'Mistress title/identity' },
  { type: 'D/s', activity: 'Owner', description: 'Consensual ownership role' },
  { type: 'D/s', activity: 'Slave', description: 'Consensual ownership/submission identity' },
  { type: 'D/s', activity: 'Property', description: 'Consensual property identity' },
  { type: 'D/s', activity: 'Service-oriented', description: 'Identity focused on service' },
  { type: 'D/s', activity: 'Caregiver', description: 'Caregiving role within an adult dynamic' },

  // ── Age-role ────────────────────────────────────────────────────────────
  { type: 'Age-role', activity: 'Little', description: 'Adult age-role identity' },
  { type: 'Age-role', activity: 'Middle', description: 'Adult age-role identity' },

  // ── Brat ────────────────────────────────────────────────────────────────
  { type: 'Brat', activity: 'Brat', description: 'Playfully resistant submissive identity' },
  { type: 'Brat', activity: 'Brat Tamer', description: 'Dominant role centred on brat dynamics' },

  // ── Primal ──────────────────────────────────────────────────────────────
  { type: 'Primal', activity: 'Hunter', description: 'Hunter identity within primal play' },
  { type: 'Primal', activity: 'Prey', description: 'Prey identity within primal play' },

  // ── Visibility ──────────────────────────────────────────────────────────
  { type: 'Visibility', activity: 'Voyeur', description: 'Watching role' },
  { type: 'Visibility', activity: 'Exhibitionist', description: 'Being-seen role' },

  // ── Financial ───────────────────────────────────────────────────────────
  { type: 'Financial', activity: 'Findom', description: 'Financial-control identity' },
  { type: 'Financial', activity: 'Financial submissive', description: 'Financial submission identity' },

  // ── Hypnosis ────────────────────────────────────────────────────────────
  { type: 'Hypnosis', activity: 'Hypnotist', description: 'Hypnosis practitioner/top' },
  { type: 'Hypnosis', activity: 'Hypno submissive', description: 'Person receiving hypnotic roleplay' },
];

export const ROLES_OPTIONS: PreferenceOption[] = [
  // ── General ─────────────────────────────────────────────────────────────
  { type: 'General', activity: 'Top', description: 'Person giving/performing in a specific scene' },
  { type: 'General', activity: 'Bottom', description: 'Person receiving in a specific scene' },
  { type: 'General', activity: 'Dominant Top', description: 'Top who also directs the scene' },
  { type: 'General', activity: 'Submissive Bottom', description: 'Bottom who also submits' },

  // ── Impact ──────────────────────────────────────────────────────────────
  { type: 'Impact', activity: 'Impact Top', description: 'Gives consensual impact' },
  { type: 'Impact', activity: 'Impact Bottom', description: 'Receives consensual impact' },

  // ── Rope ────────────────────────────────────────────────────────────────
  { type: 'Rope', activity: 'Rigger', description: 'Ties' },
  { type: 'Rope', activity: 'Rope Bottom', description: 'Is tied' },

  // ── Sensory ─────────────────────────────────────────────────────────────
  { type: 'Sensory', activity: 'Sensory Top', description: 'Controls sensory stimulation' },
  { type: 'Sensory', activity: 'Sensory Bottom', description: 'Receives sensory stimulation' },

  // ── Pet Play ────────────────────────────────────────────────────────────
  { type: 'Pet Play', activity: 'Handler', description: 'Guides a pet' },
  { type: 'Pet Play', activity: 'Pet', description: 'Takes animal role' },
  { type: 'Pet Play', activity: 'Trainer', description: 'Trains a pet' },

  // ── Primal ──────────────────────────────────────────────────────────────
  { type: 'Primal', activity: 'Hunter', description: 'Pursuing role' },
  { type: 'Primal', activity: 'Prey', description: 'Pursued role' },

  // ── Teasing ─────────────────────────────────────────────────────────────
  { type: 'Teasing', activity: 'Tease', description: 'Teasing role' },
  { type: 'Teasing', activity: 'Tease Tamer', description: 'Controls teasing dynamic' },

  // ── Service ─────────────────────────────────────────────────────────────
  { type: 'Service', activity: 'Service Dominant', description: 'Directs or receives service' },
  { type: 'Service', activity: 'Service Submissive', description: 'Provides service' },

  // ── Protocol ────────────────────────────────────────────────────────────
  { type: 'Protocol', activity: 'Protocol Dominant', description: 'Sets formal rules/protocol' },
  { type: 'Protocol', activity: 'Protocol Submissive', description: 'Follows formal rules/protocol' },

  // ── Hypnosis ────────────────────────────────────────────────────────────
  { type: 'Hypnosis', activity: 'Hypnotist', description: 'Leads hypnosis scene' },
  { type: 'Hypnosis', activity: 'Hypno Subject', description: 'Receives hypnosis scene' },

  // ── Financial ───────────────────────────────────────────────────────────
  { type: 'Financial', activity: 'Financial Dominant', description: 'Financial-control role' },
  { type: 'Financial', activity: 'Financial Submissive', description: 'Financial-submission role' },

  // ── Visibility ──────────────────────────────────────────────────────────
  { type: 'Visibility', activity: 'Voyeur', description: 'Watching role' },
  { type: 'Visibility', activity: 'Exhibitionist', description: 'Being-watched role' },
];

export const DYNAMICS_OPTIONS: PreferenceOption[] = [
  // ── D/s ─────────────────────────────────────────────────────────────────
  { type: 'D/s', activity: 'Dominant/submissive', description: 'Ongoing power-exchange relationship' },
  { type: 'D/s', activity: 'Master/slave', description: 'Master/slave dynamic' },
  { type: 'D/s', activity: 'Owner/property', description: 'Consensual ownership dynamic' },
  { type: 'D/s', activity: 'Daddy/little', description: 'Adult caregiving/authority dynamic' },
  { type: 'D/s', activity: 'Mommy/little', description: 'Adult caregiving/authority dynamic' },
  { type: 'D/s', activity: 'Caregiver/little', description: 'Adult caregiving dynamic' },
  { type: 'D/s', activity: 'Brat/brat tamer', description: 'Provocation and correction dynamic' },
  { type: 'D/s', activity: 'Service dynamic', description: 'Power exchange through service' },
  { type: 'D/s', activity: 'Protocol dynamic', description: 'Power exchange through formal rules' },
  { type: 'D/s', activity: '24/7 D/s', description: 'Power exchange extending beyond scenes' },

  // ── Primal ──────────────────────────────────────────────────────────────
  { type: 'Primal', activity: 'Hunter/prey', description: 'Primal pursuit dynamic' },

  // ── Pet Play ────────────────────────────────────────────────────────────
  { type: 'Pet Play', activity: 'Handler/pet', description: 'Animal-roleplay relationship' },

  // ── Financial ───────────────────────────────────────────────────────────
  { type: 'Financial', activity: 'Findom/paypig', description: 'Financial-control relationship' },

  // ── Non-monogamy ────────────────────────────────────────────────────────
  { type: 'Non-monogamy', activity: 'Polyamory', description: 'Consensual multi-partner relationship' },
  { type: 'Non-monogamy', activity: 'Open relationship', description: 'Consensual non-monogamy' },
  { type: 'Non-monogamy', activity: 'Swinging', description: 'Consensual partner exchange' },
  { type: 'Non-monogamy', activity: 'Cuckold/cuckquean dynamic', description: 'Consensual eroticized partner non-exclusivity' },
  { type: 'Non-monogamy', activity: 'Hotwife/hothusband dynamic', description: 'Consensual partner-sharing dynamic' },

  // ── Relationship ────────────────────────────────────────────────────────
  { type: 'Relationship', activity: 'Dominant partner', description: 'Relationship-oriented dominant role' },
  { type: 'Relationship', activity: 'Submissive partner', description: 'Relationship-oriented submissive role' },
  { type: 'Relationship', activity: 'Switching relationship', description: 'Relationship where roles may change' },
];

export interface PreferencesDraft {
  gender: string;
  attractedTo: string[];
  interestedIn: string[];
  experiencedIn: string[];
  curiousAbout: string[];
  softLimits: string[];
  hardLimits: string[];
  identities: string[];
  roles: string[];
  dynamics: string[];
  visibility: {
    gender: boolean;
    attractedTo: boolean;
    interestedIn: boolean;
    experiencedIn: boolean;
    curiousAbout: boolean;
    softLimits: boolean;
    hardLimits: boolean;
    identities: boolean;
    roles: boolean;
    dynamics: boolean;
  };
}

const emptyDraft = (): PreferencesDraft => ({
  gender: '',
  attractedTo: [],
  interestedIn: [],
  experiencedIn: [],
  curiousAbout: [],
  softLimits: [],
  hardLimits: [],
  identities: [],
  roles: [],
  dynamics: [],
  visibility: {
    gender: false,
    attractedTo: false,
    interestedIn: false,
    experiencedIn: false,
    curiousAbout: false,
    softLimits: false,
    hardLimits: false,
    identities: false,
    roles: false,
    dynamics: false,
  },
});

/**
 * Holds the member's Preferences selections, shared between the main
 * Preferences screen and the per-category "Select" panel (a separate route).
 *
 * Every change is auto-saved: callers mutate the draft and then call
 * `scheduleSave()`, which batches rapid changes and PATCHes the full
 * preferences payload to the API. There is no separate "Update" step.
 */
@Injectable({ providedIn: 'root' })
export class PreferencesService {

  private userService = inject(UserService);
  private toastr = inject(ToastrService);

  draft: PreferencesDraft = emptyDraft();
  /** True once loadFromProfile() has run — lets the panel/main screen tell
   *  "nothing loaded yet" apart from "loaded, genuinely empty". */
  loaded = false;
  /** Which account the draft belongs to, so a different login never sees
   *  (or saves over) the previous account's selections. */
  loadedUserId: string | null = null;

  /** Auto-save state, for the "Saving… / All changes saved" indicator. */
  readonly saveStatus = signal<PreferencesSaveStatus>('idle');

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  /** A change arrived while a request was in flight — save again after it. */
  private saveQueued = false;

  /** True while there are changes not yet confirmed by the server. */
  get hasPendingChanges(): boolean {
    return this.saveTimer !== null || this.inFlight || this.saveQueued;
  }

  /** Debounced auto-save — call after every change to the draft. */
  scheduleSave(): void {
    if (!this.loaded) return;
    this.saveStatus.set('saving');
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.saveNow();
    }, AUTO_SAVE_DELAY_MS);
  }

  /** Sends any pending change immediately (e.g. when leaving the screen). */
  flush(): void {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.saveNow();
    }
  }

  private saveNow(): void {
    if (this.inFlight) {
      // Requests are sent one at a time so an older payload can never land
      // after a newer one and overwrite it.
      this.saveQueued = true;
      return;
    }
    this.inFlight = true;
    this.saveStatus.set('saving');

    this.userService.updateUserProfile(this.toUpdatePayload()).subscribe({
      next: (res: any) => {
        this.inFlight = false;
        if (this.saveQueued) {
          this.saveQueued = false;
          this.saveNow();
          return;
        }
        if (res?.success === false) {
          this.saveStatus.set('error');
          this.toastr.error(res?.message || 'Failed to save preferences');
          return;
        }
        if (!this.saveTimer) this.saveStatus.set('saved');
      },
      error: (err: any) => {
        this.inFlight = false;
        this.saveQueued = false;
        this.saveStatus.set('error');
        this.toastr.error(err?.error?.message || 'Failed to save preferences');
      },
    });
  }

  /** Retry after a failed auto-save. */
  retrySave(): void {
    this.saveNow();
  }

  optionsFor(key: PreferenceCategoryKey): PreferenceOption[] {
    switch (key) {
      case 'identities': return IDENTITIES_OPTIONS;
      case 'roles':      return ROLES_OPTIONS;
      case 'dynamics':   return DYNAMICS_OPTIONS;
      default:           return SHARED_ACTIVITY_OPTIONS;
    }
  }

  metaFor(slug: string): PreferenceCategoryMeta | undefined {
    return PREFERENCE_CATEGORIES.find(c => c.slug === slug);
  }

  loadFromProfile(user: any): void {
    const prefs = user?.preferences ?? {};
    const visibility = user?.profileVisibility ?? {};
    this.draft = {
      gender: user?.gender ?? '',
      attractedTo: Array.isArray(user?.attractedTo) ? [...user.attractedTo] : [],
      interestedIn: Array.isArray(prefs.interestedIn) ? [...prefs.interestedIn] : [],
      experiencedIn: Array.isArray(prefs.experiencedIn) ? [...prefs.experiencedIn] : [],
      curiousAbout: Array.isArray(prefs.curiousAbout) ? [...prefs.curiousAbout] : [],
      softLimits: Array.isArray(prefs.softLimits) ? [...prefs.softLimits] : [],
      hardLimits: Array.isArray(prefs.hardLimits) ? [...prefs.hardLimits] : [],
      identities: Array.isArray(prefs.identities) ? [...prefs.identities] : [],
      roles: Array.isArray(prefs.roles) ? [...prefs.roles] : [],
      dynamics: Array.isArray(prefs.dynamics) ? [...prefs.dynamics] : [],
      visibility: {
        gender: !!visibility.gender,
        attractedTo: !!visibility.attractedTo,
        interestedIn: !!visibility.interestedIn,
        experiencedIn: !!visibility.experiencedIn,
        curiousAbout: !!visibility.curiousAbout,
        softLimits: !!visibility.softLimits,
        hardLimits: !!visibility.hardLimits,
        identities: !!visibility.identities,
        roles: !!visibility.roles,
        dynamics: !!visibility.dynamics,
      },
    };
    this.loaded = true;
    this.loadedUserId = user?.id ?? user?._id ?? null;
    this.saveStatus.set('idle');
  }

  /** Payload shape expected by PATCH /users/update-profile. */
  toUpdatePayload(): any {
    const d = this.draft;
    return {
      gender: d.gender,
      attractedTo: d.attractedTo,
      preferences: {
        interestedIn: d.interestedIn,
        experiencedIn: d.experiencedIn,
        curiousAbout: d.curiousAbout,
        softLimits: d.softLimits,
        hardLimits: d.hardLimits,
        identities: d.identities,
        roles: d.roles,
        dynamics: d.dynamics,
      },
      profileVisibility: { ...d.visibility },
    };
  }

  toggleValue(key: PreferenceCategoryKey, value: string): void {
    const list = this.draft[key] as string[];
    const idx = list.indexOf(value);
    if (idx === -1) list.push(value);
    else list.splice(idx, 1);
    this.scheduleSave();
  }

  isSelected(key: PreferenceCategoryKey, value: string): boolean {
    return (this.draft[key] as string[]).includes(value);
  }
}
